import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useAppDispatch } from "../app/store";
import {
  selectPinnedSubjectsList,
  selectInscribedSubjects,
  setPinnedSubjectsList,
  selectOfferedSubjects,
  addCalendarSubject,
} from "../reduxSlices/subjectsSlice";
import { selectStudent, selectToken } from "../reduxSlices/studentSlice";
import { getMateriasFijadasAlumno } from "../Api/subject";
import { IInscribedSubject } from "../types/inscribedSubjects";
import { IOfferedSubject } from "../types";
import { selectLoginCompleted, setVerFijadas } from "../reduxSlices/globalFlagsSlice";

/** idMateria (`IOfferedSubject.id`) de la oferta para un curso fijado; 0 si aún no está en oferta. */
const idMateriaFromOfferedForCurso = (
  offered: IOfferedSubject[] | null,
  idCurso: number,
  anioLectivo: number
): number => {
  if (!Array.isArray(offered)) return 0;
  for (const materia of offered) {
    const match = materia.sections?.find((sec) => sec.idCurso === idCurso && sec.anioLectivo === anioLectivo);
    if (match) return Number(materia.id);
  }
  return 0;
};

/**
 * Hook personalizado para centralizar toda la lógica de cursos fijados
 * @returns Objeto con contador de cursos fijados y función para refrescar
 */
export const usePinnedSubjects = () => {
  const dispatch = useAppDispatch();
  const pinnedSubjectsList = useSelector(selectPinnedSubjectsList);
  const inscribedSubjects = useSelector(selectInscribedSubjects);
  const student = useSelector(selectStudent);
  const offeredSubjects = useSelector(selectOfferedSubjects);
  const [offerSubjectIdsList, setofferSubjectIdsList] = useState<{ idCurso: number; anioLectivo: number }[]>([]);
  const tokenRedux = useSelector(selectToken);
  const isRefreshingPinned = useRef(false);
  const loginCompleted = useSelector(selectLoginCompleted);
  const offeredSubjectsRef = useRef(offeredSubjects);
  offeredSubjectsRef.current = offeredSubjects;

  const inscribedSubjectsRef = useRef(inscribedSubjects);
  inscribedSubjectsRef.current = inscribedSubjects;

  useEffect(() => {
    const ids: { idCurso: number; anioLectivo: number }[] = [];
    if (Array.isArray(offeredSubjects)) {
      offeredSubjects.forEach((s) => {
        if (Array.isArray(s.sections)) {
          s.sections.forEach((section) => {
            if (!section.visibleEnOferta) return;
            ids.push({ idCurso: section.idCurso, anioLectivo: section.anioLectivo });
          });
        }
      });
    }
    setofferSubjectIdsList(ids);
  }, [offeredSubjects]);

  /**
   * Calcula el contador de cursos fijados que no están inscritos
   * @param inscribedSubjects - Lista de materias inscritas
   * @param pinnedList - Lista de IDs de cursos fijados
   * @returns Número de cursos fijados no inscritos
   */
  const countPinnedNotInscribed = useCallback(
    (inscribedSubjects: IInscribedSubject[] | null, pinnedList: { idCurso: number; anioLectivo: number }[]): number => {
      let count = 0;
      if (Array.isArray(pinnedList)) {
        pinnedList.forEach((pinned) => {
          const isInOfferList = offerSubjectIdsList.some(
            (id) => id.idCurso === pinned.idCurso && id.anioLectivo === pinned.anioLectivo
          );
          const isInInscribedList = inscribedSubjects?.some(
            (id) => id.idCurso === pinned.idCurso && id.anioLectivo === pinned.anioLectivo
          );
          if (!isInInscribedList && isInOfferList) {
            count++;
          }
        });
      }

      return count;
    },
    [offerSubjectIdsList]
  );

  /**
   * Calcula el contador de cursos fijados por tipo de materia
   * @param subjects - Lista de materias ofrecidas
   * @returns Número de cursos fijados del tipo actual
   */
  const countPinnedByType = useCallback(
    (subjects: any[]): number => {
      let count = 0;
      const inscribedSubsIds = Array.isArray(inscribedSubjects) ? inscribedSubjects.map((s) => s.idCurso) : [];

      if (Array.isArray(subjects)) {
        subjects.forEach((s) => {
          if (Array.isArray(s.sections)) {
            s.sections.forEach((curso: any) => {
              const isInOfferList = offerSubjectIdsList.some((id) => id === curso.id);
              const isInInscribedList = inscribedSubsIds.some((id) => id === curso.id);
              if (
                Array.isArray(pinnedSubjectsList) &&
                pinnedSubjectsList.indexOf(curso.id) > -1 &&
                !isInInscribedList &&
                isInOfferList
              ) {
                count++;
              }
            });
          }
        });
      }

      return count;
    },
    [pinnedSubjectsList, inscribedSubjects, offerSubjectIdsList]
  );

  /**
   * Refresca la lista de cursos fijados desde el servidor
   * @returns Promise que se resuelve cuando se actualiza la lista
   */
  const refreshPinnedSubjects = useCallback(async (): Promise<void> => {
    if (!student.id) {
      console.warn("No se puede refrescar cursos fijados: ID de estudiante no disponible");
      return;
    }

    // Evitar ejecuciones simultáneas
    if (isRefreshingPinned.current) {
      console.log("🔄 refreshPinnedSubjects ya en curso, omitiendo...");
      return;
    }

    isRefreshingPinned.current = true;

    try {
      // Obtener el token del localStorage
      const token = localStorage.getItem("token");
      const fijadas: { data: { idCurso: number; anioLectivo: number }[]; status: number } =
        await getMateriasFijadasAlumno(student.id, token || tokenRedux || undefined);

      if (fijadas.status === 200 && fijadas.data !== null) {
        dispatch(setPinnedSubjectsList(fijadas.data));
        const fijadasNoInscriptas = fijadas.data.filter((curso) => {
          return !inscribedSubjectsRef.current?.some((inscrito) => inscrito.idCurso === curso.idCurso && inscrito.anioLectivo === curso.anioLectivo);
        });
        if (Array.isArray(fijadasNoInscriptas) && fijadasNoInscriptas.length > 0 && loginCompleted) {
          dispatch(setVerFijadas(true));
          fijadasNoInscriptas.forEach((curso) => {
            const idMateria = idMateriaFromOfferedForCurso(
              offeredSubjectsRef.current,
              curso.idCurso,
              curso.anioLectivo
            );
            dispatch(addCalendarSubject({ idCurso: curso.idCurso, anioLectivo: curso.anioLectivo, idMateria }));
          });
        }
        console.log("Cursos fijados actualizados correctamente");
      } else {
        console.error("Error al obtener cursos fijados:", fijadas);
      }
    } catch (error) {
      console.error("Error al refrescar cursos fijados:", error);
    } finally {
      isRefreshingPinned.current = false;
    }
  }, [student.id, tokenRedux, dispatch]);

  /**
   * Verifica si un curso específico está fijado
   * @param courseId - ID del curso a verificar
   * @returns true si el curso está fijado, false en caso contrario
   */
  const isCoursePinned = useCallback(
    (courseId: number, anioLectivo: number): boolean => {
      return (
        Array.isArray(pinnedSubjectsList) &&
        pinnedSubjectsList.find((s) => s.idCurso === courseId && s.anioLectivo === anioLectivo) !== undefined
      );
    },
    [pinnedSubjectsList]
  );

  // Contador de cursos fijados no inscritos (memoizado)
  const pinnedNotInscribedCount = useMemo(() => {
    const visiblePinnedList = Array.isArray(pinnedSubjectsList)
      ? pinnedSubjectsList
          .filter((p) => {
            if (!Array.isArray(offeredSubjects)) return false;
            return offeredSubjects.some(
              (s) =>
                Array.isArray(s.sections) &&
                s.sections.some(
                  (sec) =>
                    sec.idCurso === p.idCurso &&
                    sec.anioLectivo === p.anioLectivo &&
                    sec.visibleEnOferta
                )
            );
          })
          .map((s) => ({ idCurso: s.idCurso, anioLectivo: s.anioLectivo }))
      : [];
    return countPinnedNotInscribed(inscribedSubjects, visiblePinnedList);
  }, [countPinnedNotInscribed, inscribedSubjects, pinnedSubjectsList, offeredSubjects]);

  // Función helper para verificar si un curso está en la lista de procesos
  const isCourseInProcess = (
    subcriptionProcessSectionIds: { idCurso: number; anioLectivo: number }[],
    idCurso: number,
    anioLectivo: number
  ): boolean => {
    return subcriptionProcessSectionIds.some((item) => item.idCurso === idCurso && item.anioLectivo === anioLectivo);
  };

  return {
    // Estado
    pinnedSubjectsList,
    pinnedNotInscribedCount,

    // Funciones de utilidad
    countPinnedNotInscribed,
    countPinnedByType,
    isCoursePinned,
    isCourseInProcess,
    refreshPinnedSubjects,
  };
};

export default usePinnedSubjects;
